(function () {
    'use strict';

    function debounce(func, wait) {
        let timeout;

        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };

            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    function getSearchUrl(root, query) {
        const baseUrl = root.dataset.searchUrl || '/';
        const url = new URL(baseUrl, window.location.origin);

        url.searchParams.set('s', query);

        return url.toString();
    }

    function createHitsTemplate(useSearchPageHits) {
        const badgeClass = 'rb-search-result__badge badge badge--size_sm badge--style_01';

        return {
            item(hit, { html, components }) {
                if (useSearchPageHits) {
                    return html`
						<div class="rb-search-result__header algolia-hit">
							<h2 class="rb-search-result__title">
								<a class="rb-search-result__link" href="${hit.url}">
									${components.Highlight({ hit, attribute: 'title' })}
								</a>
                            </h2>
                                
                            <div class=${badgeClass}>
                                ${hit.post_type_label || hit.post_type}
                            </div>
                        </div>
							
                        ${hit.excerpt ? html`
                            <div class="rb-search-result__text">
                                ${components.Highlight({ hit, attribute: 'excerpt' })}
                            </div>
                        ` : ''}
					`;
                }
                return html`
					<div class="algolia-hit">
						<div class="algolia-hit__title">
							<a class="algolia-hit__link" href="${hit.url}">
								${components.Highlight({ hit, attribute: 'title' })}
							</a>
						</div>

						<div class=${badgeClass}>
							${hit.post_type}
						</div>
					</div>
				`;
            },
            empty({ query }, { html }) {
                return html`<p class="algolia__no-hits-message">No results have been found for ${query}.</p>`;
            }
        };
    }

    function initSearch(root, algoliaSearch) {
        const $ = window.jQuery;

        const applicationId = root.dataset.algoliaApplicationId;
        const apiKey = root.dataset.algoliaSearchKey;
        const collectionName = root.dataset.algoliaCollection;
        const searchTerm = root.dataset.searchTerm || '';
        const placeholderText = root.dataset.placeholder || 'How can we help?';

        const searchBoxContainer = root.querySelector('.js-algolia-searchbox');
        const resultsContainer = root.querySelector('.js-algolia-results');
        const container = root.querySelector('.js-algolia-container');
        const hitsContainer = root.querySelector('.js-algolia-hits');

        const facetsPanel = root.querySelector('.js-algolia-facets-panel');
        const facetsToggle = root.querySelector('.js-algolia-facets-toggle');
        const facetsContainer = root.querySelector('.js-algolia-facets-container');
        const facetsContainerPostType = root.querySelector('.js-algolia-facets-post-type .js-algolia-filters');
        const facetsContainerLocation = root.querySelector('.js-algolia-facets-location .js-algolia-filters');
        const facetsContainerService = root.querySelector('.js-algolia-facets-service .js-algolia-filters');
        const facetsContainerCategory = root.querySelector('.js-algolia-facets-category .js-algolia-filters');
        const $facetsPanel = $(facetsPanel);
        const $facetsToggle = $(facetsToggle);
        const facetsMediaQuery = window.matchMedia('(min-width: 68.75em)');

        const paginationContainer = root.querySelector('.js-algolia-pagination');

        const facetsCssClasses = {
            root: 'algolia-facets',
            noRefinementRoot: 'algolia-facets--empty',
            noResults: 'algolia-facets--no-results',
            list: 'algolia-facets__list',
            item: 'algolia-facets__item js-algolia-facet',
            selectedItem: 'algolia-facets__item--selected',
            label: 'algolia-facets__label',
            checkbox: 'algolia-facets__checkbox',
            labelText: 'algolia-facets__label-text',
            count: 'algolia-facets__count'
        };

        const isInPage = root.classList.contains('algolia--in-page');
        const isSiteSearch = root.classList.contains('algolia--site-search');
        const isHero = root.classList.contains('algolia--hero') && !isInPage && !isSiteSearch;
        const useSearchPageHits = isInPage || isSiteSearch;

        function hasVisibleFilters() {
            return !!(facetsContainer && facetsContainer.querySelector('.js-algolia-facet'));
        }

        function syncFacetsPanel() {
            if (!useSearchPageHits || !$facetsPanel.length || !$facetsToggle.length) {
                return;
            }

            const hasFilters = hasVisibleFilters();
            const isDesktop = facetsMediaQuery.matches;

            $facetsToggle.prop('hidden', !hasFilters || isDesktop);

            if (!hasFilters) {
                $facetsToggle.attr('aria-expanded', 'false');
                $facetsPanel.stop(true, true).hide();
                return;
            }

            if (isDesktop) {
                $facetsToggle.attr('aria-expanded', 'false');
                $facetsPanel.stop(true, true).show();
                return;
            }

            const isExpanded = $facetsToggle.attr('aria-expanded') === 'true';

            $facetsPanel.stop(true, true)[isExpanded ? 'show' : 'hide']();
        }

        if (!applicationId || !apiKey || !collectionName) {
            return;
        }

        const searchClient = algoliaSearch(applicationId, apiKey);
        const debouncedSearch = debounce(function (query, searchFn) {
            searchFn(query);
        }, 300);

        const search = instantsearch({
            indexName: collectionName,
            searchClient: searchClient,
            searchOnEmptyQuery: false,
            initialUiState: searchTerm
                ? {
                    [collectionName]: {
                        query: searchTerm
                    }
                }
                : undefined,
            searchFunction(helper) {
                const query = (helper.state.query || '').trim();

                if (query.length >= 1) {
                    helper.search();
                }
            }
        });

        const widgets = [
            instantsearch.widgets.configure({
                hitsPerPage: 12
            }),

            instantsearch.widgets.searchBox({
                container: searchBoxContainer,
                placeholder: placeholderText,
                showReset: true,
                cssClasses: {
                    root: 'algolia-search__widget',
                    form: 'algolia-search__form js-algolia-form',
                    input: 'algolia-search__input',
                    reset: 'algolia-search__reset',
                    resetIcon: 'algolia-search__reset-icon',
                    loadingIndicator: 'algolia-search__loading',
                    loadingIcon: 'algolia-search__loading-icon',
                    submit: 'algolia-search__submit',
                    submitIcon: 'algolia-search__submit-icon'
                },
                queryHook(query, searchFn) {
                    const trimmedQuery = query.trim();

                    if (trimmedQuery.length >= 1) {
                        if (resultsContainer) {
                            resultsContainer.classList.remove('is-hidden');
                        }
                        debouncedSearch(trimmedQuery, searchFn);
                    } else {
                        if (resultsContainer) {
                            resultsContainer.classList.add('is-hidden');
                        }
                        if (container) {
                            container.classList.add('is-hidden');
                        }
                        if ($facetsToggle && $facetsPanel) {
                            $facetsToggle.attr('aria-expanded', 'false').prop('hidden', true);
                            $facetsPanel.stop(true, true).hide();
                        }
                    }
                },
                templates: {
                    submit({ cssClasses }, { html }) {
                        return html`<svg class="${cssClasses.submitIcon} icon icon--search" role="presentation" focusable="false" aria-hidden="true"><use xlink:href="#svg-search"></use></svg>`;
                    },
                    reset({ cssClasses }, { html }) {
                        return html`<svg class="${cssClasses.resetIcon} icon icon--cross" role="presentation" focusable="false" aria-hidden="true"><use xlink:href="#svg-cross"></use></svg>`;
                    }
                }
            }),
            instantsearch.widgets.hits({
                container: hitsContainer,
                cssClasses: {
                    root: 'algolia-hits',
                    emptyRoot: 'algolia-hits--empty',
                    list: isInPage || isSiteSearch ? 'rb-search-results' : 'algolia-hits__list',
                    item: isInPage || isSiteSearch ? 'rb-search-result' : 'algolia-hits__item'
                },
                templates: createHitsTemplate(useSearchPageHits)
            })
        ];

        if (facetsContainerPostType) {
            widgets.push(instantsearch.widgets.refinementList({
                container: facetsContainerPostType,
                attribute: 'post_type_label',
                sortBy: ['name:asc'],
                cssClasses: facetsCssClasses
            }));
        }

        if (facetsContainerLocation) {
            widgets.push(instantsearch.widgets.refinementList({
                container: facetsContainerLocation,
                attribute: 'locations',
                sortBy: ['name:asc'],
                cssClasses: facetsCssClasses
            }));
        }

        if (facetsContainerService) {
            widgets.push(instantsearch.widgets.refinementList({
                container: facetsContainerService,
                attribute: 'areas_of_law',
                sortBy: ['name:asc'],
                cssClasses: facetsCssClasses
            }));
        }

        if (facetsContainerCategory) {
            widgets.push(instantsearch.widgets.refinementList({
                container: facetsContainerCategory,
                attribute: 'categories',
                sortBy: ['name:asc'],
                cssClasses: facetsCssClasses
            }));
        }

        if (paginationContainer) {
            widgets.push(instantsearch.widgets.pagination({
                container: paginationContainer,
                showFirst: false,
                showLast: false,
                padding: 1,
                scrollTo: resultsContainer,
                cssClasses: {
                    root: 'algolia-pagination',
                    list: 'algolia-pagination__list',
                    item: 'algolia-pagination__item',
                    previousPageItem: 'algolia-pagination__prev',
                    nextPageItem: 'algolia-pagination__next',
                    pageItem: 'algolia-pagination__page',
                    selectedItem: 'is-current',
                    disabledItem: 'is-disabled',
                    link: 'algolia-pagination__link'
                },
                templates: {
                    previous(_, { html }) {
                        return html`
                            <svg class="pagination__icon icon icon--chevron-left" role="presentation" focusable="false" aria-hidden="true">
                                <use xlink:href="#svg-chevron-left"></use>
                            </svg>
                            <span class="u-visually-hidden">Go to previous page</span>
                        `;
                    },
                    next(_, { html }) {
                        return html`
                            <svg class="pagination__icon icon icon--chevron-right" role="presentation" focusable="false" aria-hidden="true">
                                <use xlink:href="#svg-chevron-right"></use>
                            </svg>
                            <span class="u-visually-hidden">Go to next page</span>
                        `;
                    }
                }
            }));
        }

        search.addWidgets(widgets);

        let heroPortalRoot = null;
        let scheduleHeroPosition = null;

        if (isHero && resultsContainer) {
            const heroPortalMount = document.querySelector('.js-algolia-hero-portal-mount');

            heroPortalRoot = document.createElement('div');
            heroPortalRoot.className = 'algolia--hero algolia__hero-portal js-algolia-hero-portal';
            heroPortalRoot.appendChild(resultsContainer);
            heroPortalMount.appendChild(heroPortalRoot);

            if (heroPortalMount) {
                heroPortalMount.removeAttribute('hidden');
            }

            const positionHeroResults = function () {
                const form = root.querySelector('.js-algolia-form');
                if (!form) return;

                const rootRect = root.getBoundingClientRect();
                const formRect = form.getBoundingClientRect();

                resultsContainer.style.left = `${Math.round(rootRect.left)}px`;
                resultsContainer.style.width = `${Math.round(rootRect.width)}px`;
                resultsContainer.style.top = `${Math.round(formRect.bottom) - 1}px`;
            };

            scheduleHeroPosition = function () {
                window.requestAnimationFrame(positionHeroResults);
            };

            window.addEventListener('scroll', scheduleHeroPosition, { capture: true, passive: true });
            window.addEventListener('resize', scheduleHeroPosition);

            const heroResizeObserver = new ResizeObserver(scheduleHeroPosition);
            heroResizeObserver.observe(root);

            const heroResultsClassObserver = new MutationObserver(scheduleHeroPosition);
            heroResultsClassObserver.observe(resultsContainer, { attributes: true, attributeFilter: ['class'] });

            search.on('render', scheduleHeroPosition);
        }

        if ((isInPage || isSiteSearch) && $facetsPanel && $facetsToggle) {
            $facetsToggle.on('click', function () {
                if (facetsMediaQuery.matches) {
                    return;
                }

                const isExpanded = $facetsToggle.attr('aria-expanded') === 'true';

                $facetsToggle.attr('aria-expanded', isExpanded ? 'false' : 'true');
                $facetsPanel.stop(true, true)[isExpanded ? 'slideUp' : 'slideDown'](400);
            });

            search.on('render', syncFacetsPanel);
            facetsMediaQuery.addEventListener('change', syncFacetsPanel);
        }

        search.start();

        if (scheduleHeroPosition) {
            scheduleHeroPosition();
        }

        if (container && hitsContainer) {
            const observer = new MutationObserver(() => {
                const hasHits = !!hitsContainer.querySelector('.algolia-hit');
                const hasEmptyMessage = !!hitsContainer.querySelector('.algolia__no-hits-message');

                if (hasHits || hasEmptyMessage) {
                    container.classList.remove('is-hidden');
                } else {
                    container.classList.add('is-hidden');
                }
            });

            observer.observe(hitsContainer, { childList: true, subtree: true });
        }

        const form = root.querySelector('.js-algolia-searchbox .js-algolia-form');

        if (form) {
            const searchInput = form.querySelector('input');
            form.addEventListener('submit', function (e) {
                e.preventDefault();
                const query = searchInput.value.trim();
                if (query.length) {
                    window.location.href = getSearchUrl(root, query);
                }
            });
        }

        if (!isInPage && !isSiteSearch && container) {
            document.addEventListener('click', function (e) {
                const inPortal = heroPortalRoot && heroPortalRoot.contains(e.target);
                if (!root.contains(e.target) && !inPortal) {
                    container.classList.add('is-hidden');
                }
            });
        }
    }

    function init() {
        const roots = [
            ...document.querySelectorAll('.js-algolia')
        ];

        if (!roots.length) return;
        if (!window.instantsearch || !window['algoliasearch/lite']) return;

        const { liteClient: algoliaSearch } = window['algoliasearch/lite'];

        roots.forEach((root) => {
            initSearch(root, algoliaSearch);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();